/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description update provider Status Button
 */
import { Redo, DeleteOne } from '@icon-park/react';
import { Button as AntButton, App, ButtonProps, Tooltip } from 'antd';

import useDeleteProvider from 'hooks/admin/mutate/useDeleteProvider';
import useUpdateProvider from 'hooks/admin/mutate/useUpdateProvider';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';
import { PROVIDER_STATUS } from 'app-constants/admin/provider';
import { MODAL_TYPES } from '../modal';
import { useAppStoreValues } from 'store/app';

/**
 * modal content for non-billing customers (hard delete / enable)
 */
const getNonBillingModalContent = (isActiveProvider: boolean, npi: string, name: string): string | React.ReactNode => {
	if (isActiveProvider) {
		return (
			<>
				{`Are you sure you want to permanently delete NPI ${npi} — ${name}?`}
				<br />
				{'This action cannot be undone.'}
			</>
		);
	}
	return (
		<>
			{'Are you sure you want to enable the below NPI ?'}
			<br />
			{name}
		</>
	);
};

const getLabel = (isBillingCustomer: boolean, isActiveProvider: boolean): string => {
	if (isBillingCustomer) {
		return isActiveProvider ? 'Deactivate' : 'Activate';
	} else {
		return isActiveProvider ? 'Delete' : 'Enable';
	}
};

/**
 * UpdateProviderStatus
 *
 */

const UpdateProviderStatus: React.FC<{ providerDetails: Provider.IResponseModel } & ButtonProps> = ({ providerDetails, ...restProps }) => {
	const { modal } = App.useApp();
	const { mutateAsync: updateProvider } = useUpdateProvider();
	const { mutateAsync: deleteProvider } = useDeleteProvider();
	const { userAccess, setupInfo } = useAppStoreValues();
	const { canUpdateProviderStatus } = userAccess;
	const isBillingCustomer = setupInfo?.isBillingCustomer;

	const isActiveProvider = providerDetails.activeStatus === PROVIDER_STATUS.active.value;
	const disabled = restProps.disabled || !canUpdateProviderStatus;

	const label = getLabel(isBillingCustomer, isActiveProvider);
	const tooltip = canUpdateProviderStatus ? '' : NO_ACCESS_TOOLTIP_TEXT;

	const openBillingConfirm = () => {
		const isDeactivating = isActiveProvider;
		modal.confirm({
			width: 560,
			title: isDeactivating ? 'Deactivate Provider?' : 'Activate Provider?',
			content: isDeactivating
				? `Are you sure you want to deactivate ${providerDetails.name}? Once done, the provider will be removed from all active dropdown selections.`
				: `Are you sure you want to re-activate ${providerDetails.name}?`,
			okText: isDeactivating ? 'Yes, Deactivate' : 'Yes, Proceed',
			cancelText: 'Cancel',
			okButtonProps: isDeactivating ? { danger: true } : undefined,
			onOk: () =>
				updateProvider({
					providerId: providerDetails.id,
					model: {
						...providerDetails,
						activeStatus: isDeactivating ? PROVIDER_STATUS.inactive.value : PROVIDER_STATUS.active.value,
					},
				}),
		});
	};

	const openNonBillingConfirm = () => {
		modal.confirm({
			width: 560,
			title: isActiveProvider ? 'Delete NPI' : MODAL_TYPES.confirm.title,
			okText: isActiveProvider ? 'Delete' : 'Enable',
			okButtonProps: isActiveProvider ? { danger: true } : undefined,
			content: getNonBillingModalContent(isActiveProvider, providerDetails.npi, providerDetails.name),
			onOk: () => {
				if (isActiveProvider) {
					return deleteProvider({ providerId: providerDetails.id });
				}
				return updateProvider({
					providerId: providerDetails.id,
					model: { ...providerDetails, activeStatus: PROVIDER_STATUS.active.value },
				});
			},
		});
	};

	const onClick = () => {
		if (isBillingCustomer) {
			openBillingConfirm();
			return;
		}
		openNonBillingConfirm();
	};

	return (
		<Tooltip title={tooltip}>
			<AntButton
				danger={isActiveProvider}
				icon={isActiveProvider ? <DeleteOne /> : <Redo />}
				onClick={onClick}
				{...restProps}
				disabled={disabled}
			>
				{label}
			</AntButton>
		</Tooltip>
	);
};

export default UpdateProviderStatus;
